/**
 * Linux Desktop API Service
 * Handles all API calls to the Linux desktop backend
 */

import axiosInstance from "../../../utils/axios";

/**
 * List all Linux desktops for the current user
 */
export const listLinuxDesktops = async () => {
  const response = await axiosInstance.get("linux");
  return response.data;
};

/**
 * Create a new Linux desktop
 * @param {Object} params - Desktop creation parameters
 * @param {string} params.template - Template/AMI to use
 * @param {string} [params.instanceType] - EC2 instance type
 * @param {number} [params.volumeSize] - Volume size in GB
 * @param {string} [params.userGroup] - User group for billing
 */
export const createLinuxDesktop = async (params) => {
  const response = await axiosInstance.post("linux", params);
  return response.data;
};

/**
 * Get a specific Linux desktop by ID
 * @param {string} id - Desktop ID
 */
export const getLinuxDesktop = async (id) => {
  const response = await axiosInstance.get(`linux/${id}`);
  return response.data;
};

/**
 * Delete a Linux desktop
 * @param {string} id - Desktop ID
 */
export const deleteLinuxDesktop = async (id) => {
  await axiosInstance.delete(`linux/${id}`);
};

/**
 * Perform an action on a Linux desktop
 * @param {string} id - Desktop ID
 * @param {string} action - Action to perform (start, stop, terminate)
 */
export const linuxDesktopAction = async (id, action) => {
  const response = await axiosInstance.post(`linux/${id}/action`, { action });
  return response.data;
};

/**
 * Get credentials for a Linux desktop
 * @param {string} id - Desktop ID
 */
export const getLinuxCredentials = async (id) => {
  const response = await axiosInstance.get(`linux/${id}/credentials`);
  return response.data;
};

/**
 * Poll desktop status until it reaches a target state
 * @param {string} id - Desktop ID
 * @param {string[]} targetStates - Array of states to wait for
 * @param {number} [timeout=300000] - Timeout in ms (default 5 min)
 * @param {number} [interval=5000] - Polling interval in ms
 * @returns {Promise<Object>} - Desktop object when target state reached
 */
export const waitForDesktopState = async (id, targetStates, timeout = 300000, interval = 5000) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const desktop = await getLinuxDesktop(id);

    if (targetStates.includes(desktop.phase)) {
      return desktop;
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(`Timeout waiting for desktop to reach state: ${targetStates.join(", ")}`);
};

/**
 * Fetch the instance types available to the current org.
 * Returns an array of { type, family, vcpu, ram, gpu, available, unavailableReason }.
 */
export const getAvailableInstanceTypes = async () => {
  const response = await axiosInstance.get("capacity/instance-types");
  return response.data;
};

const linuxApi = {
  listLinuxDesktops,
  createLinuxDesktop,
  getLinuxDesktop,
  deleteLinuxDesktop,
  linuxDesktopAction,
  getLinuxCredentials,
  waitForDesktopState,
  getAvailableInstanceTypes
};

export default linuxApi;

/* ----------------------------- VM snapshots -----------------------------
 * EBS-backed DesktopSnapshot CRD (gomydesk.gomydesk.io/v1) — distinct from
 * the legacy /snapshots endpoint which targets container Session snapshots.
 */

/** List snapshots for one desktop. */
export const listDesktopSnapshots = async (desktopId) => {
  const response = await axiosInstance.get(`linux/${desktopId}/snapshots`);
  return response.data;
};

/** List all VM snapshots the current user owns. */
export const listAllSnapshots = async () => {
  const response = await axiosInstance.get(`linux/snapshots`);
  return response.data;
};

/** Create a snapshot of a desktop. */
export const createDesktopSnapshot = async (
  desktopId,
  { displayName, description, consistency, visibility } = {}
) => {
  const response = await axiosInstance.post(`linux/${desktopId}/snapshots`, {
    displayName,
    description,
    consistency,
    visibility
  });
  return response.data;
};

/** Delete a snapshot by name. */
export const deleteDesktopSnapshot = async (name) => {
  const response = await axiosInstance.delete(`linux/snapshots/${name}`);
  return response.data;
};

/** Restore a snapshot as a new desktop. */
export const restoreDesktopFromSnapshot = async (snapshotName, params = {}) => {
  const response = await axiosInstance.post(`linux`, {
    ...params,
    restore: { fromSnapshot: snapshotName }
  });
  return response.data;
};
